import React from "react";
import { Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Layers, Hand, BookOpen, User, LogOut, ShoppingBag } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { NAV } from "@/constants/testIds";
import BrandLogo from "@/components/BrandLogo";
import Oracle from "@/pages/Oracle";
import Journal from "@/pages/Journal";
import Profile from "@/pages/Profile";
import SoulDeckHub from "@/pages/SoulDeckHub";
import SpreadReading from "@/pages/SpreadReading";
import HandDrawn from "@/pages/HandDrawn";
import Shop from "@/pages/Shop";
import Placeholder from "@/pages/Placeholder";

const tabs = [
  { to: "/app/oracle", label: "Oracle", icon: Compass, testId: NAV.tabOracle },
  { to: "/app/deck", label: "Soul Deck", icon: Layers, testId: NAV.tabDeck },
  { to: "/app/handdrawn", label: "Hand Drawn", icon: Hand, testId: NAV.tabHandDrawn },
  { to: "/app/shop", label: "Shop", icon: ShoppingBag, testId: NAV.tabShop },
  { to: "/app/journal", label: "Journal", icon: BookOpen, testId: NAV.tabJournal },
  { to: "/app/profile", label: "Profile", icon: User, testId: NAV.tabProfile },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#080808] text-[#F4EFE6] flex flex-col lg:flex-row">
      {/* Desktop side nav */}
      <aside className="hidden lg:flex lg:w-64 shrink-0 bg-[#080808] border-r border-[#C8A96E]/10 flex-col justify-between p-8">
        <div>
          <BrandLogo className="h-14 mb-14 -ml-1" />
          <nav className="space-y-1">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testId}
                className={({ isActive }) =>
                  `flex items-center gap-4 px-3 py-3 rounded-sm font-overline transition-colors ${
                    isActive
                      ? "text-[#C8A96E] border-l border-[#C8A96E]"
                      : "text-[#F4EFE6]/60 hover:text-[#F4EFE6]"
                  }`
                }
              >
                <t.icon strokeWidth={1.25} size={18} />
                {t.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div>
          {user && (
            <div className="mb-6">
              <div className="font-overline text-[#F4EFE6]/40 mb-2">Seeker</div>
              <div className="font-heading text-lg text-[#F4EFE6]/85">{user.name || user.email}</div>
            </div>
          )}
          <button
            data-testid={NAV.logoutBtn}
            onClick={doLogout}
            className="flex items-center gap-3 text-[#F4EFE6]/50 hover:text-[#C8A96E] transition-colors font-overline"
          >
            <LogOut strokeWidth={1.25} size={16} />
            Close the veil
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div
        className="lg:hidden sticky top-0 z-40 border-b border-[#C8A96E]/10 flex items-center justify-center py-3"
        style={{ background: "rgba(8,8,8,0.92)", backdropFilter: "blur(12px)" }}
      >
        <BrandLogo className="h-9" dataTestid="brand-logo-mobile" />
      </div>

      {/* Content */}
      <main className="flex-1 min-h-screen">
        <Routes>
          <Route index element={<Navigate to="oracle" replace />} />
          <Route path="oracle" element={<Oracle />} />
          <Route path="spread/:spreadId" element={<SpreadReading />} />
          <Route path="deck" element={<SoulDeckHub />} />
          <Route path="handdrawn" element={<HandDrawn />} />
          <Route path="shop" element={<Shop />} />
          <Route path="journal" element={<Journal />} />
          <Route path="profile" element={<Profile />} />
        </Routes>
      </main>

      {/* Mobile bottom nav */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#C8A96E]/15 backdrop-blur-xl"
        style={{ background: "rgba(8,8,8,0.9)" }}
      >
        <div className="flex items-center justify-around py-3">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`${t.testId}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 text-[10px] tracking-[0.2em] uppercase ${
                  isActive ? "text-[#C8A96E]" : "text-[#F4EFE6]/55"
                }`
              }
            >
              <t.icon strokeWidth={1.25} size={20} />
              {t.label.split(" ")[0]}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}
